import type { Controls } from "./Controls";
import type { Fighter } from "../fighters/Fighter";

/** Уровень сложности AI-противника в одиночной игре. */
export type AIDifficulty = "novice" | "veteran" | "master";

interface AIProfile {
  /** [min, max] мс — раз в сколько пересматриваем стратегию */
  decisionMin: number;
  decisionMax: number;
  /** Дальность за пределами которой просто идём вперёд */
  approachDist: number;
  /** Дальность ближнего боя — ниже неё доступны атака/блок/отступ */
  closeDist: number;
  /** Вероятность что в ближнем бою выберем «атаку» */
  attackProb: number;
  /** Вероятность что в ближнем бою выберем «блок» */
  blockProb: number;
  /** Шанс нажать кнопку удара в каждом тике в state="attack" */
  attackTrigger: number;
  /** Доля punch'ей среди всех ударов (остальное — kick) */
  punchRatio: number;
  /** Шанс «комбо» — сразу второй удар подряд после первого */
  comboProb: number;
  /** Реагирует на прыжок противника блоком */
  reactToJump: boolean;
  /** При снижении HP агрессивно атакует (boost attackProb) */
  pressOnLowHp: boolean;
}

const PROFILES: Record<AIDifficulty, AIProfile> = {
  novice: {
    decisionMin: 350, decisionMax: 600,
    approachDist: 130, closeDist: 110,
    attackProb: 0.60, blockProb: 0.22,
    attackTrigger: 0.06, punchRatio: 0.65,
    comboProb: 0.05,
    reactToJump: false, pressOnLowHp: false,
  },
  veteran: {
    decisionMin: 160, decisionMax: 280,
    approachDist: 125, closeDist: 115,
    attackProb: 0.72, blockProb: 0.42,
    attackTrigger: 0.18, punchRatio: 0.55,
    comboProb: 0.45,
    reactToJump: true, pressOnLowHp: true,
  },
  master: {
    decisionMin: 70, decisionMax: 140,
    approachDist: 120, closeDist: 120,
    attackProb: 0.85, blockProb: 0.60,
    attackTrigger: 0.30, punchRatio: 0.50,
    comboProb: 0.70,
    reactToJump: true, pressOnLowHp: true,
  },
};

export const AI_DIFFICULTY_LABELS: Record<AIDifficulty, string> = {
  novice:  "🟢 НОВИЧОК",
  veteran: "🟡 ВЕТЕРАН",
  master:  "🔴 МАСТЕР",
};

/**
 * AI для P2 в одиночной игре.
 * Поведение настраивается профилем сложности.
 */
export class AIControls implements Controls {
  readonly source: string = "AI";

  private self?: Fighter;
  private opp?: Fighter;
  private state: "approach" | "attack" | "retreat" | "block" = "approach";
  private nextDecisionTime = 0;
  private currentAction: Record<string, boolean> = {};
  private prev: Record<string, boolean> = {};
  private comboQueued = false;
  private profile: AIProfile;

  constructor(private difficulty: AIDifficulty = "novice") {
    this.profile = PROFILES[difficulty];
  }

  attach(self: Fighter, opponent: Fighter) {
    this.self = self;
    this.opp = opponent;
  }

  update(time: number): void {
    this.prev = { ...this.currentAction };
    if (!this.self || !this.opp) {
      this.currentAction = {};
      return;
    }

    const dx = this.opp.body.x - this.self.body.x;
    const dist = Math.abs(dx);
    const facing = dx > 0 ? "right" : "left";
    const p = this.profile;

    // Реакция на прыжок противника — заранее блокируем (только veteran/master)
    if (p.reactToJump && this.opp.body.body && !this.opp.body.body.blocked.down && dist < 180) {
      this.currentAction = { block: true };
      return;
    }

    // Раз в decisionMin..decisionMax мс — пересматриваем стратегию
    if (time >= this.nextDecisionTime) {
      this.nextDecisionTime = time + p.decisionMin + Math.random() * (p.decisionMax - p.decisionMin);
      const r = Math.random();

      // Под низким HP — мастер/ветеран начинают «прессовать»
      const lowHpBoost = (p.pressOnLowHp && this.self.hp / this.self.hpMax < 0.35) ? 0.15 : 0;
      const attackBoost = Math.min(0.95, p.attackProb + lowHpBoost);

      if (dist > p.approachDist) {
        this.state = "approach";
      } else if (dist > p.closeDist) {
        this.state = r < 0.7 ? "approach" : "attack";
      } else {
        if (r < attackBoost) this.state = "attack";
        else if (r < attackBoost + p.blockProb) this.state = "block";
        else this.state = "retreat";
      }

      // В ближнем бою + state=attack — гарантированный удар прямо сейчас.
      // Без этого AI часто «прокручивал» state=attack без реального тыка.
      if (this.state === "attack" && dist <= p.closeDist) {
        this.comboQueued = true;
      }
    }

    const a: Record<string, boolean> = {};
    if (this.state === "approach") {
      if (facing === "right") a.right = true;
      else                    a.left  = true;
    } else if (this.state === "retreat") {
      if (facing === "right") a.left  = true;
      else                    a.right = true;
    } else if (this.state === "attack") {
      // Сразу долбим если в очереди combo
      if (this.comboQueued) {
        this.comboQueued = false;
        if (Math.random() < p.punchRatio) a.punch = true;
        else                              a.kick  = true;
      } else if (Math.random() < p.attackTrigger) {
        if (Math.random() < p.punchRatio) a.punch = true;
        else                              a.kick  = true;
        // следующая декларация — может быть combo
        if (Math.random() < p.comboProb) {
          this.comboQueued = true;
          // пере-spawn'им решение раньше — продолжаем атаковать
          this.nextDecisionTime = time + 100;
        }
      }
    } else if (this.state === "block") {
      a.block = true;
    }

    this.currentAction = a;
  }

  isLeft()  { return !!this.currentAction.left; }
  isRight() { return !!this.currentAction.right; }
  isUp()    { return !!this.currentAction.up; }
  isDown()  { return !!this.currentAction.down; }
  isPunch() { return !!this.currentAction.punch; }
  isKick()  { return !!this.currentAction.kick; }
  isBlock() { return !!this.currentAction.block; }

  justPressed(action: string): boolean {
    return !!this.currentAction[action] && !this.prev[action];
  }
}
